"use client";

import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { SubscriptionGifts } from "@/forms/subscription/SubscriptionGifts";
import { CheckoutForm } from "@/checkout/types";
import { GiftSectionFormValue } from "@/checkout/Sections/SubscriptionSection/types";

type SubscriptionGiftSectionsProps = {
  isPending?: boolean;
};

export const SubscriptionGiftSections = ({
  isPending,
}: SubscriptionGiftSectionsProps) => {
  const form = useFormContext<CheckoutForm>();
  const {
    control,
    formState: { errors },
  } = form;
  const giftSections = (
    useWatch({
      control,
      name: "giftSections",
    }) ?? []
  ).filter(
    (section): section is GiftSectionFormValue =>
      Boolean(section) && Array.isArray(section.options),
  );

  useFieldArray({
    control,
    name: "giftSections",
    rules: {
      validate: (value) => {
        const sections = (Array.isArray(value) ? value : []).filter(
          (section): section is GiftSectionFormValue =>
            Boolean(section) && Array.isArray(section.options),
        );
        for (let i = 0; i < sections.length; i++) {
          const section = sections[i];
          if (!section) {
            continue;
          }
          const selectedCount: number =
            section.options?.filter((opt) => opt.selected).length ?? 0;
          if (selectedCount !== section.count) {
            return `Seksjon ${i + 1}: Du må velge ${section.count} gave${section.count > 1 ? "r" : ""}`;
          }
        }
        return true;
      },
    },
  });

  if (!giftSections.length) {
    return null;
  }

  return (
    <fieldset className="mb-0 space-y-8" disabled={isPending}>
      {giftSections.map((section: GiftSectionFormValue, index) => {
        const sectionErrorMessage: string | undefined =
          (errors.giftSections?.[index]?.options?.root?.message as string) ??
          undefined;

        return (
          <SubscriptionGifts
            key={`gift-section-${index}`}
            count={section.count}
            items={section.options}
            fieldArrayName={`giftSections.${index}.options`}
            sectionIndex={index}
            totalSections={giftSections.length}
            sectionError={sectionErrorMessage}
          />
        );
      })}
    </fieldset>
  );
};
